import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  Button,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import {
  parseAndValidateTags,
  formatMacAddress,
  ParsedTag,
} from '../../utils/validation';
import { useDebounce } from '../../hooks/useDebounce';

// Stable empty array to avoid infinite re-renders from default prop
const EMPTY_ARRAY: string[] = [];

export interface TagEdits {
  currentTags: string[];      // All tags currently assigned (right panel)
  addedTags: string[];        // New tags (not in initialAssignedTagIds)
  removedTags: string[];      // Tags from initialAssignedTagIds that were removed
  newTagsToCreate: string[];  // Tags that need to be created at Stage 1 first
  hasErrors: boolean;         // Whether there are validation errors
}

interface TagIdManagerProps {
  /** Tags that are already assigned to this location for this product (source of truth from API) */
  initialAssignedTagIds?: string[];
  /** Tags assigned to the product but not to any location (available to pull in) */
  availableTagIds?: string[];
  /** Called when user edits tags - reports the delta from initialAssignedTagIds */
  onTagEditsChange?: (edits: TagEdits) => void;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Whether this is an edit (vs create) */
  isEdit?: boolean;
}

export const TagIdManager: React.FC<TagIdManagerProps> = ({
  initialAssignedTagIds = EMPTY_ARRAY,
  availableTagIds = EMPTY_ARRAY,
  onTagEditsChange,
  disabled = false,
  isEdit = false,
}) => {
  // Left panel state - for typing new tags
  const [inputText, setInputText] = React.useState('');
  const [parsedInputTags, setParsedInputTags] = React.useState<ParsedTag[]>([]);
  const [selectedNewTags, setSelectedNewTags] = React.useState<Set<string>>(new Set());

  // Available tags from product (not at any location) - for "pull" feature
  const [selectedAvailable, setSelectedAvailable] = React.useState<Set<string>>(new Set());

  // Right panel state - the source of truth for what will be assigned to this location
  const [assignedTags, setAssignedTags] = React.useState<string[]>([]);

  // Track initialization
  const initializedFromRef = React.useRef<string[] | null>(null);

  const debouncedInput = useDebounce(inputText, 300);

  // Stable callback ref
  const onTagEditsChangeRef = React.useRef(onTagEditsChange);
  React.useEffect(() => {
    onTagEditsChangeRef.current = onTagEditsChange;
  }, [onTagEditsChange]);

  // Initialize assigned tags when initialAssignedTagIds changes
  React.useEffect(() => {
    const prevIds = initializedFromRef.current;
    const currentIds = initialAssignedTagIds;

    const hasChanged = prevIds === null ||
      prevIds.length !== currentIds.length ||
      prevIds.some((id, i) => id !== currentIds[i]);

    if (hasChanged) {
      setAssignedTags([...currentIds]);
      setInputText('');
      setParsedInputTags([]);
      setSelectedNewTags(new Set());
      setSelectedAvailable(new Set());
      initializedFromRef.current = currentIds;
    }
  }, [initialAssignedTagIds]);

  // Parse input and update parsed tags
  React.useEffect(() => {
    if (!debouncedInput.trim()) {
      setParsedInputTags([]);
      setSelectedNewTags(new Set());
      return;
    }

    // Combine all "already known" tags to filter duplicates
    const allKnownTags = [
      ...assignedTags,
      ...availableTagIds,
    ];

    const parsed = parseAndValidateTags(debouncedInput, allKnownTags, []);
    setParsedInputTags(parsed);

    // Auto-select all valid tags so user can assign with one click
    const validTags = parsed
      .filter(t => t.isValid && !t.isDuplicate)
      .map(t => t.formatted);
    setSelectedNewTags(new Set(validTags));
  }, [debouncedInput, assignedTags, availableTagIds]);

  // Compute and report edits whenever assigned tags change
  React.useEffect(() => {
    const initialSet = new Set(initialAssignedTagIds.map(t => t.toUpperCase()));
    const availableSet = new Set(availableTagIds.map(t => t.toUpperCase()));
    const assignedSet = new Set(assignedTags.map(t => t.toUpperCase()));

    // Added: in assigned but not in initial
    const addedTags = assignedTags.filter(t => !initialSet.has(t.toUpperCase()));

    // Removed: in initial but not in assigned
    const removedTags = initialAssignedTagIds.filter(t => !assignedSet.has(t.toUpperCase()));

    // New tags to create: added tags that are not in the available pool
    // These need to be created at Stage 1 (tag-to-product) first
    const newTagsToCreate = addedTags.filter(t => !availableSet.has(t.toUpperCase()));

    onTagEditsChangeRef.current?.({
      currentTags: assignedTags,
      addedTags,
      removedTags,
      newTagsToCreate,
      hasErrors: false,
    });
  }, [assignedTags, initialAssignedTagIds, availableTagIds]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  };

  const toggleNewTagSelection = (tag: string) => {
    if (disabled) return;
    setSelectedNewTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  };

  const toggleAvailableSelection = (tag: string) => {
    if (disabled) return;
    setSelectedAvailable(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  };

  // Assign new tags from input text
  const handleAssignNew = () => {
    if (disabled || selectedNewTags.size === 0) return;

    const selectedArray = Array.from(selectedNewTags);
    setAssignedTags(prev => [...prev, ...selectedArray]);

    // Remove assigned tags from input text
    const assignedUpperSet = new Set([
      ...assignedTags.map(t => t.toUpperCase()),
      ...selectedArray.map(t => t.toUpperCase()),
    ]);

    const remainingLines = inputText
      .split('\n')
      .filter(line => {
        const trimmed = line.trim();
        if (!trimmed) return false;
        const mac = formatMacAddress(trimmed).toUpperCase();
        if (mac.includes(':')) {
          return !assignedUpperSet.has(mac);
        }
        return true;
      });

    setInputText(remainingLines.join('\n'));
    setSelectedNewTags(new Set());
  };

  // Pull available tags (already assigned to product but not at any location)
  const handlePullAvailable = () => {
    if (disabled || selectedAvailable.size === 0) return;

    const selectedArray = Array.from(selectedAvailable);
    setAssignedTags(prev => [...prev, ...selectedArray]);
    setSelectedAvailable(new Set());
  };

  const handleRemoveAssigned = (tag: string) => {
    if (disabled) return;
    setAssignedTags(prev => prev.filter(t => t.toUpperCase() !== tag.toUpperCase()));
  };

  const handleRemoveFromInput = (tag: ParsedTag) => {
    if (disabled) return;
    const lines = inputText.split('\n');
    const newLines = lines.filter(line => {
      const trimmed = line.trim();
      return trimmed !== tag.original && trimmed !== tag.formatted;
    });
    setInputText(newLines.join('\n'));
    setSelectedNewTags(prev => {
      const newSet = new Set(prev);
      newSet.delete(tag.formatted);
      return newSet;
    });
  };

  // Filter available tags to only show those not yet assigned
  const unassignedAvailableTags = availableTagIds.filter(
    tag => !assignedTags.some(a => a.toUpperCase() === tag.toUpperCase())
  );

  const validNewTags = parsedInputTags.filter(t => t.isValid && !t.isDuplicate);
  const invalidTags = parsedInputTags.filter(t => !t.isValid || t.isDuplicate);
  const hasValidNewSelected = selectedNewTags.size > 0;
  const hasAvailableSelected = selectedAvailable.size > 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Tag ID Management
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, minHeight: 350 }}>
        {/* Left Panel - Add New / Pull Available */}
        <Paper variant="outlined" sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
          {/* Section 1: Available Tags to Pull (from product, not at any location) */}
          {unassignedAvailableTags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Available from Product ({unassignedAvailableTags.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Tags assigned to product but not at any location. Click to select, then "Pull →"
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 100, overflow: 'auto' }}>
                {unassignedAvailableTags.map((tag, index) => {
                  const isSelected = selectedAvailable.has(tag);
                  return (
                    <Chip
                      key={`available-${tag}-${index}`}
                      label={tag}
                      size="small"
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => toggleAvailableSelection(tag)}
                      sx={{ cursor: 'pointer' }}
                      aria-label={`Available tag ${tag}${isSelected ? ', selected' : ''}`}
                    />
                  );
                })}
              </Box>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={handlePullAvailable}
                disabled={disabled || !hasAvailableSelected}
                sx={{ mt: 1 }}
              >
                Pull ({selectedAvailable.size})
              </Button>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* Section 2: Add New Tags */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Add New Tags
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Enter new tag IDs (one per line). These will be created and assigned.
          </Typography>

          <TextField
            label="Enter Tag IDs"
            multiline
            rows={3}
            value={inputText}
            onChange={handleInputChange}
            fullWidth
            disabled={disabled}
            placeholder="AA:BB:CC:DD:EE:FF&#10;11:22:33:44:55:66"
            size="small"
            sx={{ mb: 2 }}
          />

          {/* Valid new tags - selectable */}
          {validNewTags.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Click to select, then "Assign →"
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {validNewTags.map((tag, index) => {
                  const isSelected = selectedNewTags.has(tag.formatted);
                  return (
                    <Chip
                      key={`new-${tag.formatted}-${index}`}
                      label={tag.formatted}
                      size="small"
                      color={isSelected ? 'primary' : 'success'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      icon={<AddIcon />}
                      onClick={() => toggleNewTagSelection(tag.formatted)}
                      onDelete={disabled ? undefined : () => handleRemoveFromInput(tag)}
                      deleteIcon={<DeleteIcon />}
                      sx={{ cursor: 'pointer' }}
                      aria-label={`New tag ${tag.formatted}${isSelected ? ', selected' : ''}`}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Invalid tags */}
          {invalidTags.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="error">
                Invalid tags:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {invalidTags.map((tag, index) => (
                  <Chip
                    key={`invalid-${tag.original}-${index}`}
                    label={`${tag.formatted || tag.original} - ${tag.error || 'Invalid'}`}
                    size="small"
                    color="error"
                    variant="outlined"
                    icon={<ErrorIcon />}
                    onDelete={disabled ? undefined : () => handleRemoveFromInput(tag)}
                    deleteIcon={<DeleteIcon />}
                    aria-label={`Invalid tag ${tag.original}: ${tag.error || 'Invalid format'}`}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Assign new button */}
          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleAssignNew}
              disabled={disabled || !hasValidNewSelected}
              fullWidth
            >
              Assign New ({selectedNewTags.size})
            </Button>
          </Box>
        </Paper>

        {/* Right Panel - Assigned to this Location */}
        <Paper variant="outlined" sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Assigned to Location ({assignedTags.length})
          </Typography>

          {assignedTags.length === 0 ? (
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'text.disabled',
            }}>
              <Typography variant="body2">
                No tags assigned to this location yet
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}>
              {assignedTags.map((tag, index) => {
                const isFromInitial = initialAssignedTagIds.some(
                  t => t.toUpperCase() === tag.toUpperCase()
                );
                const isPulled = availableTagIds.some(
                  t => t.toUpperCase() === tag.toUpperCase()
                ) && !isFromInitial;
                
                let chipColor: 'info' | 'success' | 'warning' = 'success';
                let label = 'new';
                if (isFromInitial) {
                  chipColor = 'info';
                  label = 'existing';
                } else if (isPulled) {
                  chipColor = 'warning';
                  label = 'pulled';
                }

                return (
                  <Chip
                    key={`assigned-${tag}-${index}`}
                    label={tag}
                    size="small"
                    color={chipColor}
                    variant="filled"
                    onDelete={disabled ? undefined : () => handleRemoveAssigned(tag)}
                    deleteIcon={<DeleteIcon />}
                    sx={{ justifyContent: 'space-between' }}
                    aria-label={`Assigned tag ${tag}, ${label}`}
                  />
                );
              })}
            </Box>
          )}

          {/* Summary */}
          {assignedTags.length > 0 && (
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {initialAssignedTagIds.length > 0 && (
                  <Typography variant="caption" color="info.main">
                    {assignedTags.filter(t => 
                      initialAssignedTagIds.some(i => i.toUpperCase() === t.toUpperCase())
                    ).length} existing
                  </Typography>
                )}
                <Typography variant="caption" color="warning.main">
                  {assignedTags.filter(t => 
                    !initialAssignedTagIds.some(i => i.toUpperCase() === t.toUpperCase()) &&
                    availableTagIds.some(a => a.toUpperCase() === t.toUpperCase())
                  ).length} pulled
                </Typography>
                <Typography variant="caption" color="success.main">
                  {assignedTags.filter(t => 
                    !initialAssignedTagIds.some(i => i.toUpperCase() === t.toUpperCase()) &&
                    !availableTagIds.some(a => a.toUpperCase() === t.toUpperCase())
                  ).length} new
                </Typography>
                {initialAssignedTagIds.filter(t =>
                  !assignedTags.some(a => a.toUpperCase() === t.toUpperCase())
                ).length > 0 && (
                  <Typography variant="caption" color="error">
                    {initialAssignedTagIds.filter(t =>
                      !assignedTags.some(a => a.toUpperCase() === t.toUpperCase())
                    ).length} to remove
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
